import { useCallback } from "react"
import { GAMES } from "pages/games/common/constants"
import { CONTENT_TYPES } from "pages/student/activity/common/constants"

const resolveIsCorrect = (ans) => {
  const nestedAns = ans?.answer?.[0] || ans?.Answer?.[0]
  if (ans?.isCorrect !== undefined) return ans.isCorrect
  if (nestedAns?.Status !== undefined) return nestedAns.Status
  return ans?.IsCorrect || false
}

const parseMetadata = (metaStr) => {
  if (!metaStr) return null
  try {
    return typeof metaStr === "string" ? JSON.parse(metaStr) : metaStr
  } catch {
    return null
  }
}

const extractMetadataString = (ans) => {
  const nestedAns = ans?.answer?.[0] || ans?.Answer?.[0]
  return (
    nestedAns?.Metadata || nestedAns?.metadata || ans?.metadata || ans?.Metadata
  )
}

const extractPlayerAnswer = (rawAnswer, exercise) => {
  const rawAnsList = rawAnswer?.answer || []
  const optionAnswer = rawAnsList.length > 0 ? rawAnsList[0] : null

  if (exercise?.options?.length > 1) {
    if (!optionAnswer) return ""
    const optId =
      optionAnswer.gameSessionContentId || optionAnswer.GameSessionContentId
    const playerSelectedOption = exercise.options.find(
      (opt) => opt.gameSessionContentId === optId
    )
    return playerSelectedOption?.text || ""
  }

  if (optionAnswer) {
    return optionAnswer.Answer || optionAnswer.answer || ""
  }
  return ""
}

const resolveExplanation = (explanation) => {
  if (typeof explanation === "string") return explanation
  return explanation?.text || ""
}

const findParticipant = (participants, userId) => {
  const player = participants?.find((p) => p.userId === userId)
  return {
    player,
    participantId: player?.gameSessionParticipantId || -1,
  }
}

const isSentenceScramble = (gameId, currentGame) =>
  gameId === GAMES.SENTENCE_SCRAMBLE ||
  currentGame?.id === GAMES.SENTENCE_SCRAMBLE

const formatSentenceScramblePartial = (answersFromPlayer) =>
  answersFromPlayer.map((ans) => {
    const metaStr = extractMetadataString(ans)
    const isCorrectVal = resolveIsCorrect(ans)

    return {
      ...ans,
      gameSessionContentId:
        ans.gameSessionContentId || ans.GameSessionContentId,
      metadata: metaStr,
      isCorrect: isCorrectVal,
      correctAnswer: ans.correctAnswer || "",
      userSelected: ans.userSelected || ans.UserSelected || "",
      audioURL: ans.audioURL || "",
      explanation: "",
    }
  })

const formatSentenceScrambleFinal = (answersFromPlayer, processed) => {
  return processed.map((exercise) => {
    const matchingAns = answersFromPlayer.find(
      (ans) =>
        (ans.gameSessionContentId || ans.GameSessionContentId) ===
        exercise.gameSessionContentId
    )

    let userSelected = ""
    let isCorrectValue = false

    if (matchingAns) {
      isCorrectValue = resolveIsCorrect(matchingAns)
      const rawAnsList = matchingAns?.answer || matchingAns?.Answer || []
      const nestedAnswers = Array.isArray(rawAnsList) ? rawAnsList : []

      const placedWords = []
      nestedAnswers.forEach((na) => {
        try {
          const meta = parseMetadata(na.metadata || na.Metadata)
          const pos = meta?.placedPosition
          const wordObj = exercise.wordObjects?.find(
            (w) => w.contentId === na.contentId || w.contentId === na.ContentId
          )
          if (pos !== undefined && pos >= 0 && wordObj) {
            placedWords[pos] = wordObj.word
          }
        } catch (e) {
          // ignore
        }
      })
      userSelected = placedWords.filter(Boolean).join(" ")
      if (!userSelected) {
        userSelected =
          matchingAns.userSelected || matchingAns.UserSelected || ""
      }
    }

    return {
      ...matchingAns,
      question: exercise.question || "",
      isCorrect: isCorrectValue,
      correctAnswer: exercise.correctSentence || "",
      audioURL: exercise.audioURL || "",
      userSelected,
      explanation: resolveExplanation(exercise.explanation),
    }
  })
}

const extractValidWordIds = (answersFromPlayer) => {
  for (const ans of answersFromPlayer) {
    if (ans?.metadata) {
      const meta = parseMetadata(ans.metadata)
      if (Array.isArray(meta?.validWords)) {
        return meta.validWords
      }
    }
  }
  return null
}

const formatWordsPartial = (answersFromPlayer, wordsList) =>
  answersFromPlayer.map((ans) => {
    const word = wordsList.find(
      (w) => w.gameSessionContentId === ans.gameSessionContentId
    )
    return {
      ...ans,
      isCorrect: ans.isCorrect || false,
      correctAnswer: word?.word || "",
      audioURL: word?.audioURL,
      userSelected: ans.isCorrect ? word?.word : "",
      explanation: "",
    }
  })

const formatWordsFinal = (answersFromPlayer, wordsList) =>
  wordsList.map((word) => {
    const matchingAns = answersFromPlayer.find(
      (ans) => ans.gameSessionContentId === word.gameSessionContentId
    )
    return {
      ...matchingAns,
      isCorrect: matchingAns?.isCorrect || false,
      correctAnswer: word?.word || "",
      audioURL: word?.audioURL,
      userSelected: matchingAns?.isCorrect ? word?.word : "",
      explanation: "",
    }
  })

const formatGeneralPartial = (answersFromPlayer, processed) =>
  answersFromPlayer.map((ans) => {
    const exercise = processed.find(
      (item) => item.gameSessionContentId === ans.gameSessionContentId
    )
    const playerAnswer = extractPlayerAnswer(ans, exercise)

    return {
      ...ans,
      question: exercise?.question || "",
      isCorrect: ans.isCorrect || false,
      correctAnswer: exercise?.correctSentence || "",
      audioURL: exercise?.audioURL || "",
      userSelected: playerAnswer || "",
      explanation: resolveExplanation(exercise?.explanation),
    }
  })

const formatGeneralFinal = (answersFromPlayer, processed, gameSessionData) =>
  processed.map((exercise) => {
    const matchingAns = answersFromPlayer.find(
      (ans) => ans.gameSessionContentId === exercise.gameSessionContentId
    )
    const playerAnswer = extractPlayerAnswer(matchingAns, exercise)

    const isConversationLab =
      exercise?.audioAnswerURL !== undefined || gameSessionData?.gameId === 12
    let userSelected = playerAnswer || ""
    if (isConversationLab) {
      userSelected = ""
      const meta = parseMetadata(matchingAns?.metadata)
      userSelected = meta?.transcript || ""
    }

    return {
      ...matchingAns,
      question: exercise?.question || "",
      isCorrect: matchingAns?.isCorrect || false,
      correctAnswer:
        exercise?.correctSentence ||
        exercise?.options?.find((opt) => opt.isAnswer)?.text ||
        "",
      audioURL: isConversationLab
        ? exercise?.audioAnswerURL || ""
        : exercise?.audioURL || "",
      userSelected,
      explanation: resolveExplanation(exercise?.explanation),
    }
  })

const isWordMatch = (gameId, currentGame) =>
  gameId === GAMES.WORD_MATCH ||
  currentGame?.id === GAMES.WORD_MATCH

const formatWordMatchPartial = (answersFromPlayer, processed) => {
  const contents = processed[0]?.contents || []
  const leftItems = contents.filter(
    (item) => item.contentTypeId === CONTENT_TYPES.WORD
  )
  return answersFromPlayer.map((ans) => {
    const word = leftItems.find(
      (w) => w.gameSessionContentId === ans.gameSessionContentId
    )
    return {
      ...ans,
      isCorrect: ans.isCorrect || false,
      correctAnswer: word?.text || "",
      audioURL: word?.audioURL,
      userSelected: ans.isCorrect ? word?.text : "",
      explanation: "",
    }
  })
}

const formatWordMatchFinal = (answersFromPlayer, processed) => {
  const contents = processed[0]?.contents || []
  const leftItems = contents.filter(
    (item) => item.contentTypeId === CONTENT_TYPES.WORD
  )
  return leftItems.map((word) => {
    const matchingAns = answersFromPlayer.find(
      (ans) => ans.gameSessionContentId === word.gameSessionContentId
    )
    return {
      ...matchingAns,
      isCorrect: matchingAns?.isCorrect || false,
      correctAnswer: word?.text || "",
      audioURL: word?.audioURL,
      userSelected: matchingAns?.isCorrect ? word?.text : "",
      explanation: "",
    }
  })
}

const useGameAnswerFlow = ({
  gameSessionData,
  currentGame,
  exercisesContent,
  exerciseContentIndex,
  setAnswers,
  setGameSessionData,
  setIsLoadingGameContent,
  setIsGameFinished,
  setNextContent,
  setGameSessionAnswer,
  setGameSessionStatus,
  setGameSessionNextContent,
  userData,
  currentAnswerTime,
  playGoodAnswer,
  playWrongAnswer,
}) => {
  const formatOnlyAnswered = useCallback(
    (answersFromPlayer = [], processed = []) => {
      if (isSentenceScramble(gameSessionData?.gameId, currentGame)) {
        return formatSentenceScramblePartial(answersFromPlayer)
      }
      if (isWordMatch(gameSessionData?.gameId, currentGame)) {
        return formatWordMatchPartial(answersFromPlayer, processed)
      }

      const wordsList = processed[0]?.words || []
      if (wordsList.length > 0) {
        return formatWordsPartial(answersFromPlayer, wordsList)
      }

      return formatGeneralPartial(answersFromPlayer, processed)
    },
    [gameSessionData, currentGame]
  )

  const formatPlayerAnswers = useCallback(
    (answersFromPlayer = [], processed = []) => {
      if (isSentenceScramble(gameSessionData?.gameId, currentGame)) {
        return formatSentenceScrambleFinal(answersFromPlayer, processed)
      }
      if (isWordMatch(gameSessionData?.gameId, currentGame)) {
        return formatWordMatchFinal(answersFromPlayer, processed)
      }

      const validWordIds = extractValidWordIds(answersFromPlayer)
      let wordsList = processed[0]?.words || []
      if (validWordIds && wordsList.length > 0) {
        wordsList = wordsList.filter((word) =>
          validWordIds.includes(word.gameSessionContentId)
        )
      }

      if (wordsList.length > 0) {
        return formatWordsFinal(answersFromPlayer, wordsList)
      }

      let exercisesToFormat = processed
      if (validWordIds) {
        exercisesToFormat = processed.filter((exercise) =>
          validWordIds.includes(exercise.gameSessionContentId)
        )
      }

      return formatGeneralFinal(
        answersFromPlayer,
        exercisesToFormat,
        gameSessionData
      )
    },
    [gameSessionData, currentGame]
  )

  const sendAnswer = useCallback(
    async ({
      gameSessionContentId,
      isCorrect,
      grade = 0,
      answers = [],
      metadata = "",
      extraData = {},
      autoSlide = true,
      files = [],
      playSound = true,
      forceAutoSlide = false,
      answerTime,
    }) => {
      const { gameSessionId, participants } = gameSessionData
      const { participantId } = findParticipant(participants, userData?.userId)

      if (playSound) {
        if (playSound === "good" || (playSound === true && isCorrect)) {
          playGoodAnswer()
        } else if (
          playSound === "wrong" ||
          (playSound === true && !isCorrect)
        ) {
          playWrongAnswer()
        }
      }

      try {
        const response = await setGameSessionAnswer({
          gameId: currentGame?.id,
          gameSessionId,
          gameSessionParticipantId: participantId,
          gameSessionContentId,
          isCorrect,
          grade,
          answers,
          metadata,
          files,
        })
        if (!response?.status) return

        const answer = response?.data?.answer
        const currentExercise = exercisesContent[exerciseContentIndex]
        const playerAnswer = extractPlayerAnswer(answer, currentExercise)

        const newAnswer = {
          ...answer,
          answerTime: answerTime || currentAnswerTime,
          question: currentExercise?.question || "",
          correctAnswer: currentExercise?.correctSentence || "",
          audioURL: currentExercise?.audioURL,
          userSelected: playerAnswer,
          isCorrect: answer?.isCorrect || false,
          explanation: resolveExplanation(currentExercise?.explanation),
          ...extraData,
        }

        setAnswers((prev) => [...prev, newAnswer])

        const currentPlayer = participants?.find(
          (p) => p.userId === userData?.userId
        )
        if (currentPlayer) {
          setGameSessionData((prevSessionData) => {
            const prevParticipants = prevSessionData?.participants || []
            const updatedParticipants = prevParticipants.map((p) =>
              p.userId === userData?.userId
                ? { ...p, points: p.points + answer.points }
                : p
            )
            return {
              ...prevSessionData,
              participants: updatedParticipants,
            }
          })
        }

        const currentExerciseIndex = exerciseContentIndex
        if (response?.status && autoSlide && currentExerciseIndex >= 0) {
          const nextIndex = currentExerciseIndex + 1
          if (nextIndex < exercisesContent.length) {
            const nextContentId =
              exercisesContent[nextIndex].gameSessionContentId
            await setGameSessionNextContent({
              gameSessionContentId: nextContentId,
              gameSessionId,
              gameId: currentGame?.id,
              gameSessionParticipantId: participantId,
            })
          }
        }

        const isLastAnswer =
          currentExerciseIndex === exercisesContent.length - 1
        if (
          (autoSlide && isCorrect && !isLastAnswer) ||
          (forceAutoSlide && !isLastAnswer)
        ) {
          setTimeout(() => {
            setIsLoadingGameContent(true)
            setNextContent()
          }, 1000)
        }

        if (
          (autoSlide && isCorrect && isLastAnswer) ||
          (forceAutoSlide && isLastAnswer)
        ) {
          await setGameSessionStatus({
            gameSessionId,
            gameSessionParticipantId: participantId,
            gameSessionStatusId: 4,
          })
          setTimeout(() => {
            setIsGameFinished(true)
          }, 1000)
        }
      } catch (error) {
        console.log("[sendAnswer error]", error)
        alert(error)
      }
    },
    [
      currentGame,
      gameSessionData,
      userData?.userId,
      exercisesContent,
      exerciseContentIndex,
      currentAnswerTime,
    ]
  )

  const setNextContentFn = useCallback(async () => {
    const nextIndex = exerciseContentIndex + 1
    if (nextIndex < exercisesContent.length) {
      return { action: "next" }
    } else {
      const { gameSessionId, participants } = gameSessionData
      const { participantId } = findParticipant(participants, userData?.userId)
      return {
        action: "finish",
        gameSessionId,
        participantId,
      }
    }
  }, [exerciseContentIndex, exercisesContent, gameSessionData, userData])

  return {
    formatOnlyAnswered,
    formatPlayerAnswers,
    sendAnswer,
    setNextContentFn,
  }
}

export default useGameAnswerFlow
